import { Injectable } from '@angular/core';
import { Database, ref, listVal, update } from '@angular/fire/database';
import { Observable, firstValueFrom, of } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { filter, switchMap, map, catchError } from 'rxjs/operators';
import { User } from '../../shared/models/user.interface';

export interface Notification {
  id: string;
  message: string;
  link: string;
  timestamp: number;
  isRead: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  constructor(private db: Database, private authService: AuthService) { }

  getNotifications(): Observable<Notification[]> {
    return this.authService.currentUser$.pipe(
      filter((user): user is User => user !== null),
      switchMap(user => {
        const notificationsRef = ref(this.db, `notifications/${user.uid}`);
        return listVal<Notification>(notificationsRef, { keyField: 'id' }).pipe(
          map(notifications => notifications.sort((a, b) => b.timestamp - a.timestamp)),
          catchError(error => {
            console.error('Error fetching notifications:', error);
            return of([]); // Return an empty array to prevent the loop
          })
        );
      })
    );
  }

  async markAsRead(notificationId: string): Promise<void> {
    const user = await firstValueFrom(this.authService.currentUser$);
    if (user) {
      const notificationRef = ref(this.db, `notifications/${user.uid}/${notificationId}`);
      return update(notificationRef, { isRead: true });
    }
    return Promise.resolve();
  }

  async markAllAsRead(): Promise<void> {
    const user = await firstValueFrom(this.authService.currentUser$);
    if (user) {
      const notificationsRef = ref(this.db, `notifications/${user.uid}`);
      const notifications = await firstValueFrom(listVal<Notification>(notificationsRef, { keyField: 'id' }));
      const updates: { [key: string]: any } = {};
      notifications.forEach(n => {
        if (!n.isRead) {
          updates[`/${n.id}/isRead`] = true;
        }
      });
      return update(notificationsRef, updates);
    }
    return Promise.resolve();
  }
}
